import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';

import {
  fetchItemImages,
  fetchSimilarImages,
  resetAllImages,
  setHoveredImageSrc,
} from '../../store/templateItems';

import KeyboardProvider from '../KeyboardProvider';
import LensImage from '../LensImage';
import ImageRowCarousel from './ImageRowCarousel';

import { KEYS } from '../../utils/keycode';
import { findSrcOfHoveredImage } from '../../utils/sidebarImageSelector';

import styles from './styles.module.css';

const SIMILAR_CHUNK_SIZE = 20;

const ImageInfiniteSelector = ({
  item, onImageClick, scrollableTarget, disableKeyboard, onSimilarImageClick, minimized,
}) => {
  const dispatch = useDispatch();

  const similarImages = useSelector((store) => store.templateItems.similarImages);
  const images = useSelector((store) => store.templateItems.images);
  const offset = useSelector((store) => store.templateItems.imagesOffset);
  const hasMore = useSelector((store) => store.templateItems.imagesMore);
  const currentItemChanges = useSelector((store) => store.templateItems.currentItemChanges);

  const [rowIndex, setRowIndex] = useState(0);
  const [columnIndex, setColumnIndex] = useState(0);
  const [visibleSimilarCount, setVisibleSimilarCount] = useState(SIMILAR_CHUNK_SIZE);
  const prevItem = useRef();
  const prevScrollTarget = useRef();
  const prevAllSimilarShown = useRef(true);

  // Similar items come unpaginated, so they are revealed chunk by chunk on scroll.
  // The search results are only rendered once every similar item is on the screen.
  const visibleSimilar = similarImages.slice(0, visibleSimilarCount);
  const allSimilarShown = visibleSimilarCount >= similarImages.length;
  const visibleImages = allSimilarShown ? images : [];

  const rowIndexWithOffset = rowIndex >= visibleSimilar.length
    ? rowIndex - visibleSimilar.length
    : rowIndex;

  // Lens effect
  useEffect(() => {
    const onHover = (e) => {
      const { id } = e.target;
      const src = findSrcOfHoveredImage(id, images, similarImages);

      dispatch(setHoveredImageSrc(src));
    };

    const onMouseLeave = () => {
      dispatch(setHoveredImageSrc(''));
    };

    const target = document.getElementById(scrollableTarget);
    target?.addEventListener('mouseover', onHover);
    target?.addEventListener('mouseleave', onMouseLeave);

    return () => {
      target?.removeEventListener('mouseover', onHover);
      target?.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [similarImages, images]);

  useEffect(() => {
    const resetScroll = () => {
      const container = document.getElementById(scrollableTarget);
      if (container) {
        container.scrollTop = 0;
      }
    };

    const loadImages = async (scrollResetNeed) => {
      await Promise.all([
        dispatch(fetchSimilarImages({ id: item.id })),
        dispatch(fetchItemImages({ id: item.id, offset: 0 })),
      ]);

      if (scrollResetNeed) {
        resetScroll();
      }
    };

    const resetImages = () => {
      setRowIndex(0);
      setColumnIndex(0);
      setVisibleSimilarCount(SIMILAR_CHUNK_SIZE);
      resetScroll();
      dispatch(resetAllImages());
    };

    if (item) {
      if (currentItemChanges?.completed ?? item.completed) {
        resetImages();
      } else {
        let scrollResetNeed = false;
        if (item.id !== prevItem.current?.id) {
          setRowIndex(0);
          setColumnIndex(0);
          setVisibleSimilarCount(SIMILAR_CHUNK_SIZE);
          scrollResetNeed = true;
        }
        prevItem.current = item;
        loadImages(scrollResetNeed);
      }
      prevItem.current = item;
    }
  }, [item, scrollableTarget]);

  useEffect(() => {
    const id = `images-${rowIndex}`;
    const target = document.getElementById(id);
    if (target) {
      if (prevScrollTarget.current?.id === id) {
        return;
      }

      prevScrollTarget.current = target;

      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [columnIndex, rowIndex, rowIndexWithOffset, images, similarImages, visibleSimilarCount]);

  // A late /similar response unmounts the search rows, so a selection made on them
  // would silently end up on an unrelated similar row.
  useEffect(() => {
    if (prevAllSimilarShown.current && !allSimilarShown) {
      setRowIndex(0);
      setColumnIndex(0);
    }

    prevAllSimilarShown.current = allSimilarShown;
  }, [allSimilarShown]);

  const rowAt = (index) => (index < similarImages.length
    ? similarImages[index]
    : visibleImages[index - similarImages.length]);

  // The row being moved onto may hold fewer images than the one being left.
  const selectRow = (index) => {
    setRowIndex(index);

    const row = rowAt(index);
    if (row) {
      setColumnIndex((column) => Math.max(0, Math.min(column, row.images.length - 1)));
    }
  };

  const loadMore = () => {
    if (!allSimilarShown) {
      setVisibleSimilarCount((count) => count + SIMILAR_CHUNK_SIZE);
      return;
    }

    dispatch(fetchItemImages({ id: item.id, offset }));
  };

  const handleKeyDown = (e) => {
    if (disableKeyboard) {
      return;
    }

    switch (e.code) {
      case KEYS.ARROW_UP:
        e.preventDefault();
        selectRow(Math.max(0, rowIndex - 1));
        break;
      case KEYS.ARROW_DOWN: {
        e.preventDefault();
        const lastRow = visibleSimilar.length + visibleImages.length - 1;
        if (rowIndex >= lastRow && !allSimilarShown) {
          setVisibleSimilarCount((count) => count + SIMILAR_CHUNK_SIZE);
          selectRow(Math.min(similarImages.length - 1, rowIndex + 1));
          break;
        }
        selectRow(Math.max(0, Math.min(lastRow, rowIndex + 1)));
        break;
      }
      case KEYS.ARROW_LEFT:
        e.preventDefault();
        if (minimized) {
          break;
        }
        setColumnIndex(Math.max(0, columnIndex - 1));
        break;
      case KEYS.ARROW_RIGHT: {
        e.preventDefault();
        if (minimized) {
          break;
        }
        const selectedRow = rowAt(rowIndex);
        if (selectedRow) {
          setColumnIndex(Math.min(selectedRow.images.length - 1, columnIndex + 1));
        }
        break;
      }
      case KEYS.ENTER: {
        e.preventDefault();
        if (rowIndex < visibleSimilar.length) {
          onSimilarImageClick(visibleSimilar[rowIndex].id);
          break;
        }

        const image = visibleImages[rowIndexWithOffset]?.images[columnIndex];
        if (image) {
          onImageClick(image.id);
        }
        break;
      }
      default:
        break;
    }
  };

  const isSameSequence = (templateItem, similarImage) => (
    templateItem?.product_id
    && templateItem.product_id === similarImage.product_id
  ) || (
    templateItem?.template_item_image_group_id
    && templateItem.template_item_image_group_id === similarImage.template_item_image_group_id
  );

  return (
    <KeyboardProvider onKeyDown={handleKeyDown}>
      <InfiniteScroll
        scrollableTarget={scrollableTarget}
        dataLength={visibleSimilarCount + similarImages.length + images.length}
        next={loadMore}
        hasMore={!allSimilarShown || hasMore}
        className={styles.container}
      >
        <div className={cx(styles.similar, !similarImages.length && styles.invisible)}>
          {minimized && visibleSimilar.map((i, row) => (
            <ImageRowCarousel
              key={i.id}
              images={i.images.map(
                (it, idx) => ({ url: it, similarity: idx === 0 ? i.similarity : undefined }),
              )}
              rowId={`images-${row}`}
              currentRowId={i.id}
              imageClassName={styles.image}
              onImageClick={() => onSimilarImageClick(i.id)}
              rowClassName={cx(
                !disableKeyboard && row === rowIndex && styles.selectedRow,
              )}
              similar
            />
          ))}
          {!minimized && visibleSimilar.map((i, row) => (
            <div
              key={i.id}
              className={cx(
                styles.row,
                styles.similarRow,
                !disableKeyboard && row === rowIndex && styles.selectedRow,
                isSameSequence(item, i) && styles.choosenRow,
              )}
              onClick={() => onSimilarImageClick(i.id)}
            >
              <div
                className={styles.imagesRow}
                id={`images-${row}`}
              >
                {i.images.map((image, idx) => (
                  <LensImage
                    className={cx(styles.image)}
                    imageUrl={image}
                    bigImageUrl={i.medium_images[idx]}
                    position={idx > 2 ? 'left' : 'right'}
                    id={`similar-images-${i.id}-${idx}`}
                    similarity={idx === 0 ? i.similarity : undefined}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
        {minimized && visibleImages.map((i, row) => (
          <ImageRowCarousel
            key={i.id}
            images={i.images}
            rowId={`images-${row + visibleSimilar.length}`}
            currentRowId={i.id}
            imageClassName={styles.image}
            onImageClick={onImageClick}
            rowClassName={cx(
              !disableKeyboard && row + visibleSimilar.length === rowIndex && styles.selectedRow,
            )}
          />
        ))}
        {!minimized && visibleImages.map((i, row) => (
          <div
            key={i.id}
            className={cx(
              styles.row,
              !disableKeyboard && row + visibleSimilar.length === rowIndex && styles.selectedRow,
              i.id === item?.product_id && styles.choosenRow,
            )}
          >
            <div
              className={cx(styles.imagesRow, minimized && styles.imagesRowSmall)}
              id={`images-${row + visibleSimilar.length}`}
            >
              {i.images.map((image, column) => (
                <LensImage
                  className={cx(
                    styles.image,
                    !disableKeyboard
                    && columnIndex === column
                    && row + visibleSimilar.length === rowIndex
                    && styles.imageSelected,
                  )}
                  onClick={() => onImageClick(image.id)}
                  position={column > 2 ? 'left' : 'right'}
                  imageUrl={image.url}
                  bigImageUrl={image.medium_url}
                  similarity={image.similarity}
                  key={`images-${i.id}-${image.id}`}
                  id={`images-${i.id}-${image.id}`}
                />
              ))}
            </div>
            <p className={cx(styles.rowBrands, 'text-secondary')}>
              <span><strong>{i.name}</strong></span>
              <span>{` – ${i.brand}`}</span>
            </p>
          </div>
        ))}
      </InfiniteScroll>
    </KeyboardProvider>
  );
};

ImageInfiniteSelector.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.number.isRequired,
    product_images: PropTypes.arrayOf.isRequired,
    product_id: PropTypes.number.isRequired,
    template_item_image_group_id: PropTypes.number.isRequired,
    completed: PropTypes.number.isRequired,
  }),
  onImageClick: PropTypes.func.isRequired,
  onSimilarImageClick: PropTypes.func.isRequired,
  scrollableTarget: PropTypes.string.isRequired,
  disableKeyboard: PropTypes.bool,
  minimized: PropTypes.bool,
};

ImageInfiniteSelector.defaultProps = {
  disableKeyboard: false,
  item: null,
  minimized: false,
};

export default ImageInfiniteSelector;
